import React from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  makeStyles,
  IconButton,
  Link,
} from "@material-ui/core";
import { Close, Settings, Storage } from "@material-ui/icons";
import { useCookieConsent } from "../../contexts/CookieConsentContext";

const useStyles = makeStyles(theme => ({
  backdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 9998,
    backdropFilter: "blur(4px)",
  },
  banner: {
    position: "fixed",
    bottom: theme.spacing(2),
    left: theme.spacing(2),
    right: theme.spacing(2),
    width: "calc(100% - 32px)",
    maxWidth: "100%",
    backgroundColor: "#1a1a1a",
    border: "2px solid #FE5A97",
    borderRadius: "12px",
    padding: theme.spacing(2),
    zIndex: 9999,
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
    [theme.breakpoints.down("sm")]: {
      bottom: theme.spacing(1),
      left: theme.spacing(1),
      right: theme.spacing(1),
      width: "calc(100% - 16px)",
      padding: theme.spacing(1.5),
    },
  },
  content: {
    paddingTop: "43px",
    width: "100%",
    margin: "0 auto",
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: theme.spacing(1.5),
    },
  },
  text: {
    flex: 1,
    minWidth: "300px",
    color: "#fff",
    [theme.breakpoints.down("sm")]: {
      minWidth: "unset",
      width: "100%",
      marginBottom: theme.spacing(1),
    },
  },
  title: {
    color: "#FE5A97",
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
      marginBottom: theme.spacing(0.5),
    },
  },
  description: {
    color: "#ccc",
    fontSize: "14px",
    lineHeight: 1.4,
    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
      lineHeight: 1.5,
    },
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      flexDirection: "column",
      gap: theme.spacing(1),
      "& > *": {
        width: "100%",
        margin: "0 !important",
      },
    },
  },
  acceptButton: {
    backgroundColor: "#FE5A97",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#e04a7a",
    },
    minWidth: "120px",
    [theme.breakpoints.down("sm")]: {
      minWidth: "unset",
      width: "100%",
      padding: theme.spacing(1.25),
      fontSize: "14px",
    },
  },
  rejectButton: {
    backgroundColor: "transparent",
    color: "#FE5A97",
    border: "1px solid #FE5A97",
    "&:hover": {
      backgroundColor: "rgba(254, 90, 151, 0.1)",
    },
    minWidth: "120px",
    [theme.breakpoints.down("sm")]: {
      minWidth: "unset",
      width: "100%",
      padding: theme.spacing(1.25),
      fontSize: "14px",
    },
  },
  settingsButton: {
    backgroundColor: "transparent",
    color: "#ccc",
    border: "1px solid #666",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
    minWidth: "120px",
    [theme.breakpoints.down("sm")]: {
      minWidth: "unset",
      width: "100%",
      padding: theme.spacing(1.25),
      fontSize: "14px",
    },
  },
  closeButton: {
    color: "#ccc",
    padding: theme.spacing(0.5),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
      minWidth: "44px",
      minHeight: "44px",
    },
  },
  link: {
    color: "#FE5A97",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
    [theme.breakpoints.down("sm")]: {
      wordBreak: "break-word",
    },
  },
}));

interface CookieConsentBannerProps {
  onShowPreferences: () => void;
}

const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({
  onShowPreferences,
}) => {
  const classes = useStyles();
  const { acceptAll, rejectAll, hideBanner } = useCookieConsent();

  const handleAcceptAll = () => {
    acceptAll();
  };

  const handleRejectAll = () => {
    rejectAll();
  };

  const handleShowPreferences = () => {
    onShowPreferences();
  };

  const handleClose = () => {
    hideBanner();
  };

  return (
    <>
      <Box className={classes.backdrop} onClick={handleClose} />
      <Paper className={classes.banner} elevation={8}>
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          size="small"
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 1,
          }}
        >
          <Close />
        </IconButton>
        <Box className={classes.content}>
          <Box className={classes.text}>
            <Typography variant="h6" className={classes.title}>
              <Storage fontSize="small" />
              Cookie Consent
            </Typography>
            <Typography className={classes.description}>
              We use cookies to enhance your experience, analyze site usage, and
              assist in our marketing efforts. By clicking "Accept All", you
              consent to our use of cookies. You can also{" "}
              <Link
                href="/cookie-policy"
                className={classes.link}
                target="_blank"
              >
                learn more about our cookie policy
              </Link>{" "}
              or customize your preferences.
            </Typography>
          </Box>

          <Box className={classes.actions}>
            <Button
              variant="contained"
              className={classes.acceptButton}
              onClick={handleAcceptAll}
              fullWidth
            >
              Accept All
            </Button>
            <Button
              variant="outlined"
              className={classes.rejectButton}
              onClick={handleRejectAll}
              fullWidth
            >
              Reject All
            </Button>
            <Button
              variant="outlined"
              className={classes.settingsButton}
              onClick={handleShowPreferences}
              startIcon={<Settings />}
              fullWidth
            >
              Settings
            </Button>
          </Box>
        </Box>
      </Paper>
    </>
  );
};

export default CookieConsentBanner;
